import * as React from 'react';
import { FluentProviderProps } from '@fluentui/react-provider';
import { useThemeStyleTag } from '@fluentui/react-provider/lib/components/FluentProvider/useThemeStyleTag';
import {
    ProviderContext,
    TooltipContext,
    ThemeContext,
    ThemeClassNameContext,
} from '@fluentui/react-shared-contexts';
import { BrandVariants, createDarkTheme, createLightTheme, Theme } from '@fluentui/react-theme';
import { useConst } from '@fluentui/react-utilities';
import { isCurrentCultureRightToLeft } from 'owa-localize';
import { getIsDarkTheme } from 'owa-fabric-theme';
import { getPaletteAsRawColors } from 'owa-theme';
import { observer } from 'mobx-react-lite';
import { ColorInput, TinyColor } from '@ctrl/tinycolor';
import { IPalette } from '@fluentui/style-utilities';

function mixColors(colorA: ColorInput, colorB: ColorInput, amount?: number) {
    return new TinyColor(colorA).mix(new TinyColor(colorB), amount).toHexString();
}

function mixByPairs(colors: IPalette): BrandVariants {
    return {
        10: colors.themeDarker,
        20: mixColors(colors.themeDarker, colors.themeDark),
        30: colors.themeDark,
        40: mixColors(colors.themeDark, colors.themeDarkAlt),
        50: colors.themeDarkAlt,
        60: mixColors(colors.themeDarkAlt, colors.themePrimary),
        70: colors.themePrimary,
        80: mixColors(colors.themePrimary, colors.themeSecondary),
        90: colors.themeSecondary,
        100: mixColors(colors.themeSecondary, colors.themeTertiary),
        110: colors.themeTertiary,
        120: mixColors(colors.themeTertiary, colors.themeLight),
        130: colors.themeLight,
        140: mixColors(colors.themeLight, colors.themeLighter),
        150: colors.themeLighter,
        160: mixColors(colors.themeLighter, colors.themeLighterAlt),
    };
}

function useV9BrandTheme() {
    const palette = getPaletteAsRawColors() as IPalette;
    const isDarkMode = getIsDarkTheme();

    return React.useMemo<Theme>(() => {
        const brandVariant = mixByPairs(palette);
        return (isDarkMode ? createDarkTheme : createLightTheme)(brandVariant);
    }, [palette, isDarkMode]);
}

// The <FluentProvider> adds an unwanted div to host the CSS variables.
// Until Fluent provides an official way to separate the two concerns
// (hosting CSS Variable values and wrapping React contexts), implementing
// a workaround.
export const FluentContext = observer(function FluentContext({
    dir = isCurrentCultureRightToLeft() ? 'rtl' : 'ltr',
    targetDocument = window.document,
    children,
}: Pick<FluentProviderProps, 'theme' | 'dir' | 'targetDocument' | 'children'>) {
    const theme = useV9BrandTheme();
    const themeClassName = useThemeStyleTag({
        theme: theme,
        targetDocument,
    });

    const provider = React.useMemo(() => ({ dir, targetDocument }), [dir, targetDocument]);
    // "Tooltip" component mutates an object in this context, instance should be stable
    const tooltip = useConst({});
    return (
        <ProviderContext.Provider value={provider}>
            <ThemeContext.Provider value={theme}>
                <ThemeClassNameContext.Provider value={themeClassName}>
                    <TooltipContext.Provider value={tooltip}>{children}</TooltipContext.Provider>
                </ThemeClassNameContext.Provider>
            </ThemeContext.Provider>
        </ProviderContext.Provider>
    );
});
